import React, { useState, useEffect, useRef } from 'react';
import { formatCurrency } from '../utils/pensionCalc';

// ReconciliationFooter — sits below the Payroll Entry Summary table.
// Period total (auto-computed) | BrightPay total (admin pastes) | Match indicator.
// Debounces saves to the parent so each keystroke doesn't fire a request.
export default function ReconciliationFooter({
  periodTotal = 0,
  initialBrightPayTotal = null,
  recordedAt = null,
  disabled = false,
  onSave,                    // (number | null) => Promise<void>
}) {
  const initialString = initialBrightPayTotal != null
    ? Number(initialBrightPayTotal).toFixed(2)
    : '';
  const [input, setInput] = useState(initialString);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(recordedAt);
  const lastSavedRef = useRef(initialBrightPayTotal);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInput(initialBrightPayTotal != null ? Number(initialBrightPayTotal).toFixed(2) : '');
    lastSavedRef.current = initialBrightPayTotal;
    setSavedAt(recordedAt);
  }, [initialBrightPayTotal, recordedAt]);

  // Parse the input — strip currency symbols and grouping commas.
  const parsed = (() => {
    if (input == null || input.trim() === '') return null;
    const cleaned = input.replace(/[£,\s]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : NaN;
  })();

  const isValid = parsed !== null && !Number.isNaN(parsed);
  // Garbage input parses to NaN; treat it as no-input so we don't render
  // "Mismatch — Difference: £0.00" against a meaningless value.
  const hasInput = isValid;
  const diff = isValid ? parsed - periodTotal : null;
  const matched = isValid && Math.abs(diff) < 0.01;

  function scheduleSave(value) {
    if (!onSave) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (value === lastSavedRef.current) return;
      try {
        setSaving(true);
        await onSave(value);
        lastSavedRef.current = value;
        if (value !== null) setSavedAt(new Date().toISOString());
        else setSavedAt(null);
      } finally {
        setSaving(false);
      }
    }, 600);
  }

  function handleChange(e) {
    const raw = e.target.value;
    setInput(raw);
    const cleaned = raw.replace(/[£,\s]/g, '');
    if (cleaned === '') { scheduleSave(null); return; }
    const n = Number(cleaned);
    if (Number.isFinite(n)) scheduleSave(Math.round(n * 100) / 100);
  }

  function handleBlur() {
    if (!isValid) return;
    setInput(parsed.toFixed(2));
  }

  return (
    <div className="bg-white ring-1 ring-gray-200 rounded-xl p-5 grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
      <div>
        <p className="text-[14px] uppercase tracking-wider font-bold text-gray-500 mb-1.5">Period total</p>
        <p className="text-[32px] 2xl:text-[38px] font-bold text-ink tabular leading-none">{formatCurrency(periodTotal)}</p>
        <p className="text-[14px] text-gray-500 mt-1.5 italic">Sum of Gross Pay above</p>
      </div>
      <div>
        <label htmlFor="brightpay-total" className="text-[14px] uppercase tracking-wider font-bold text-gray-500 mb-1.5 block">
          BrightPay total
        </label>
        <div className="flex items-baseline gap-2">
          <span className="text-[26px] 2xl:text-[34px] font-bold text-ink tabular">£</span>
          <input
            id="brightpay-total"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={input}
            onChange={handleChange}
            onBlur={handleBlur}
            disabled={disabled}
            className="text-[32px] 2xl:text-[38px] font-bold tabular leading-none border-0 border-b-2 border-brand-300 focus:border-brand-600 focus:ring-0 p-0 bg-transparent w-full disabled:opacity-60"
            style={{ fontFamily: 'Lexend, system-ui' }}
          />
        </div>
        <p className="text-[14px] text-gray-500 mt-1.5 italic">
          {saving ? 'Saving…' : savedAt ? `Saved ${new Date(savedAt).toLocaleString('en-GB')}` : 'Paste from BrightPay payroll summary'}
        </p>
      </div>
      <div className="flex items-center justify-center md:justify-end">
        {!hasInput ? (
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-gray-50 ring-1 ring-gray-200">
            <span className="w-10 h-10 rounded-full bg-gray-300 grid place-items-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </span>
            <div>
              <p className="text-[18px] font-bold text-gray-700 leading-none">Awaiting input</p>
              <p className="text-[14px] text-gray-500 mt-1">Paste BrightPay total to reconcile</p>
            </div>
          </div>
        ) : matched ? (
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-emerald-50 ring-1 ring-emerald-200">
            <span className="w-10 h-10 rounded-full bg-emerald-500 grid place-items-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            <div>
              <p className="text-[18px] font-bold text-emerald-900 leading-none">Matched</p>
              <p className="text-[14px] text-emerald-700 mt-1">Difference: {formatCurrency(0)}</p>
            </div>
          </div>
        ) : (
          <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl bg-rose-50 ring-1 ring-rose-200">
            <span className="w-10 h-10 rounded-full bg-rose-500 grid place-items-center">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </span>
            <div>
              <p className="text-[18px] font-bold text-rose-900 leading-none">Mismatch</p>
              <p className="text-[14px] text-rose-700 mt-1">
                Difference: {diff > 0 ? '+' : ''}{formatCurrency(diff)}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
